import './styles/theme.css';
import './styles/main.css';

import { Navbar } from './components/Navbar';
import { LandingPage } from './components/LandingPage';
import { PreJoinRoom } from './components/PreJoinRoom';
import { MeetingRoom } from './components/MeetingRoom';
import { WebinarViewer } from './components/WebinarViewer';

class App {
  private appContainer: HTMLElement;
  private preJoinInstance: PreJoinRoom | null = null;
  private meetingInstance: MeetingRoom | null = null;

  constructor() {
    this.appContainer = document.getElementById('app') as HTMLElement;
    window.addEventListener('popstate', () => this.handleRoute());
  }

  public init() {
    this.purgeServiceWorkers();
    this.handleRoute();
  }

  private handleRoute() {
    const path = window.location.pathname;

    if (path.startsWith('/webinar/')) {
      const webinarId = path.split('/webinar/')[1]?.split('?')[0]?.split('#')[0];
      if (webinarId) {
        this.cleanupCurrent();
        const viewer = new WebinarViewer(webinarId, this.appContainer);
        viewer.init();
        return;
      }
    }

    if (path.startsWith('/room/')) {
      const parts = path.split('/room/');
      const roomId = parts[1]?.split('?')[0]?.split('#')[0];
      if (roomId) {
        this.showLobby(roomId);
        return;
      }
    }

    this.showLanding();
  }

  public showLanding() {
    this.cleanupCurrent();
    window.history.pushState({}, '', '/');
    this.renderLandingPage();
  }

  private renderLandingPage() {
    const landing = new LandingPage();
    landing.render(this.appContainer);

    // Mount Navbar in nav-mount
    const navMount = document.getElementById('nav-mount');
    if (navMount) {
      const navbar = new Navbar();
      navbar.onLanguageChanged = () => this.renderLandingPage();
      navbar.render(navMount);
    }

    landing.onNavigateToRoom = (roomId, hostToken) => {
      this.showLobby(roomId, hostToken);
    };
  }

  public showLobby(roomId: string, hostToken?: string) {
    this.cleanupCurrent();
    if (window.location.pathname !== `/room/${roomId}`) {
      window.history.pushState({}, '', `/room/${roomId}${window.location.search}`);
    }

    this.preJoinInstance = new PreJoinRoom(roomId, hostToken);
    this.preJoinInstance.init(this.appContainer);

    this.preJoinInstance.onJoinSuccess = (joinData, localStream, isVideoMuted, isAudioMuted) => {
      this.showMeeting(joinData, localStream, isVideoMuted, isAudioMuted);
    };

    this.preJoinInstance.onBack = () => {
      this.showLanding();
    };
  }

  public showMeeting(joinData: any, localStream: MediaStream | null, isVideoMuted = false, isAudioMuted = false) {
    if (this.preJoinInstance) {
      this.preJoinInstance.detachMedia();
      this.preJoinInstance = null;
    }
    if (this.meetingInstance) {
      this.meetingInstance.cleanup();
      this.meetingInstance = null;
    }

    this.meetingInstance = new MeetingRoom(joinData, localStream, isVideoMuted, isAudioMuted);
    this.meetingInstance.init(this.appContainer);

    this.meetingInstance.onLeave = () => {
      this.showLanding();
    };
  }

  private cleanupCurrent() {
    if (this.preJoinInstance) {
      this.preJoinInstance.cleanup();
      this.preJoinInstance = null;
    }
    if (this.meetingInstance) {
      this.meetingInstance.cleanup();
      this.meetingInstance = null;
    }
  }

  private purgeServiceWorkers() {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const reg of registrations) {
          reg.unregister();
        }
      }).catch(() => {});
    }
    if ('caches' in window) {
      caches.keys().then((keys) => {
        for (const key of keys) {
          caches.delete(key);
        }
      }).catch(() => {});
    }
  }
}

// Start application
const app = new App();
app.init();
